import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CloseOutlined } from '@mui/icons-material';
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import type { ISurveyProvisionalEmissionAggregateRow } from '../../../shared/services/survey/survey.model';

function fmtNum(n: number | null | undefined, digits = 6): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1) return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });
  return n.toLocaleString(undefined, { maximumSignificantDigits: 8 });
}

function hasPositiveAggregateKg(row: ISurveyProvisionalEmissionAggregateRow): boolean {
  const v = row.totalProvisionalKgCo2e;
  return v != null && Number.isFinite(v) && v > 0;
}

function sortAggregateRowsForCommit(list: ISurveyProvisionalEmissionAggregateRow[]): ISurveyProvisionalEmissionAggregateRow[] {
  const withKg = list.filter(hasPositiveAggregateKg).sort(
    (a, b) => (b.totalProvisionalKgCo2e ?? 0) - (a.totalProvisionalKgCo2e ?? 0),
  );
  const withoutKg = list.filter(r => !hasPositiveAggregateKg(r)).sort(
    (a, b) => (a.priority ?? 0) - (b.priority ?? 0),
  );
  return [...withKg, ...withoutKg];
}

type Props = {
  open: boolean;
  surveyId: string | null;
  onClose: () => void;
  onCommitted: () => void;
  onNotify: (message: string, isError?: boolean) => void;
};

export const SurveyCommitEmissionsDialog = ({
  open,
  surveyId,
  onClose,
  onCommitted,
  onNotify,
}: Props) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);
  const [rows, setRows] = useState<ISurveyProvisionalEmissionAggregateRow[]>([]);
  const [plannedEmissionCount, setPlannedEmissionCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !surveyId) {
      setRows([]);
      setSessionCount(0);
      setPlannedEmissionCount(0);
      setError(null);
      setLoading(false);
      setCommitting(false);
      return;
    }
    let cancelled = false;
    const svc = SurveyAdminService.getInstance();
    setLoading(true);
    setError(null);
    svc.getSurveyCommitEmissionsPreview(surveyId)
      .then((data) => {
        if (cancelled) return;
        setSessionCount(typeof data.sessionCount === 'number' ? data.sessionCount : 0);
        setRows(data.rows ?? []);
        setPlannedEmissionCount(typeof data.plannedEmissionCount === 'number' ? data.plannedEmissionCount : 0);
      })
      .catch((err: { response?: { data?: { message?: string } } }) => {
        if (!cancelled) {
          setError(err?.response?.data?.message ?? 'Could not load commit preview.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, surveyId]);

  const sortedRows = useMemo(() => sortAggregateRowsForCommit(rows), [rows]);

  const totalKgAcrossRules = rows.reduce((s, r) => {
    const v = r.totalProvisionalKgCo2e;
    if (v != null && Number.isFinite(v) && v > 0) return s + v;
    return s;
  }, 0);

  const canCommit = Boolean(surveyId) && sessionCount > 0 && plannedEmissionCount > 0 && !error && !loading;

  const handleCommit = async () => {
    if (!surveyId || !canCommit) return;
    setCommitting(true);
    try {
      const svc = SurveyAdminService.getInstance();
      await svc.commitSurveyEmissions(surveyId);
      onNotify('Emissions committed to the report period.');
      onCommitted();
      onClose();
    } catch (err: unknown) {
      const msg =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
          ? (err as { response: { data: { message: string } } }).response.data.message
          : 'Commit failed.';
      onNotify(msg, true);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={committing ? undefined : onClose} maxWidth='md' fullWidth aria-labelledby='commit-emissions-title'>
      <DialogTitle id='commit-emissions-title' sx={{ pr: 5 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6' component='span' fontWeight={600}>
            Commit emissions to report period
          </Typography>
          <IconButton onClick={() => !committing && onClose()} aria-label='Close' disabled={committing} sx={{ mr: -0.75 }}>
            <CloseOutlined />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          This uses <strong>submitted</strong> responses that are not yet committed (
          <strong>{sessionCount}</strong> session{sessionCount === 1 ? '' : 's'}
          ).
          {' '}One activity emission row will be created per rule with a conversion and positive aggregated output (see below).
          {' '}Responses included here will be marked committed so they are not submitted again from this survey.
        </Typography>
        {loading && (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner />
          </Box>
        )}
        {error && !loading && (
          <Typography color='error.main' variant='body2'>{error}</Typography>
        )}
        {!loading && !error && sessionCount === 0 && (
          <Typography color='text.secondary' variant='body2'>
            No eligible submitted responses: everything may already be committed, or nobody has submitted yet.
          </Typography>
        )}
        {!loading && !error && sessionCount > 0 && plannedEmissionCount === 0 && (
          <Typography color='warning.main' variant='body2'>
            There are {sessionCount} uncommitted submission{sessionCount === 1 ? '' : 's'}, but no aggregated activity with a conversion to persist.
            Configure rules or check combined provisional emissions.
          </Typography>
        )}
        {!loading && !error && sessionCount > 0 && plannedEmissionCount > 0 && rows.some(hasPositiveAggregateKg) && (
          <Typography variant='body2' sx={{ mb: 2 }}>
            <Typography component='span' variant='caption' color='text.secondary'>Estimated total from preview (same method as provisional)&nbsp;</Typography>
            <Typography component='span' fontWeight={700} color='primary.main'>
              {fmtNum(totalKgAcrossRules)} kg CO₂e
            </Typography>
          </Typography>
        )}
        {!loading && !error && plannedEmissionCount > 0 && (
          <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
            Emissions to create: {plannedEmissionCount}
          </Typography>
        )}
        {!loading && !error && sortedRows.length > 0 && (
          <Stack spacing={2}>
            {sortedRows.map(row => (
              <Box
                key={row.ruleId}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                }}
              >
                <Typography variant='subtitle2' fontWeight={600}>
                  {row.ruleName || 'Unnamed rule'}
                  <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 1 }}>
                    priority {row.priority}
                  </Typography>
                </Typography>
                {hasPositiveAggregateKg(row) ? (
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    <Typography variant='body2' color='text.secondary'>
                      Applied in <strong>{row.responsesApplied}</strong> of {sessionCount} uncommitted response
                      {sessionCount === 1 ? '' : 's'}.
                    </Typography>
                    <Typography variant='body2'>
                      <Typography component='span' variant='caption' color='text.secondary'>Sum of rule outputs (activity)&nbsp;</Typography>
                      <Typography component='span' fontWeight={600}>{fmtNum(row.totalFormulaOutput)}</Typography>
                      {row.activityUnitName ? (
                        <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
                          {row.activityUnitName}
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography variant='body2'>
                      <Typography component='span' variant='caption' color='text.secondary'>Conversion&nbsp;</Typography>
                      {row.conversionDetailSummary ?? '—'}
                    </Typography>
                    <Typography variant='body2'>
                      <Typography component='span' variant='caption' color='text.secondary'>Combined provisional (preview)&nbsp;</Typography>
                      <Typography component='span' fontWeight={700} color='primary.main'>
                        {row.totalProvisionalKgCo2e != null && Number.isFinite(row.totalProvisionalKgCo2e)
                          ? `${fmtNum(row.totalProvisionalKgCo2e)} kg CO₂e`
                          : '—'}
                      </Typography>
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                    Applied in <strong>{row.responsesApplied}</strong> of {sessionCount} uncommitted response
                    {sessionCount === 1 ? '' : 's'}.
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={committing}>Cancel</Button>
        <LoadingButton variant='contained' loading={committing} disabled={!canCommit} onClick={handleCommit}>
          Confirm commit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
